import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import {
  setJournalEntries,
  setEntriesLoading,
  setEntriesError,
  setCurrentEntry,
  addJournalEntry,
  updateJournalEntry,
  postJournalEntry
} from '../../redux/accounting';

const JournalEntries = () => {
  const dispatch = useDispatch();
  const { 
    journalEntries, 
    entriesLoading, 
    entriesError, 
    currentEntry,
    chartOfAccounts 
  } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    description: '',
    reference: '',
    date: new Date().toISOString().split('T')[0],
    entries: []
  });

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadJournalEntries();
    }
  }, [currentSchool]);

  const loadJournalEntries = async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    dispatch(setEntriesLoading(true));
    
    try {
      const data = await accountingService.getJournalEntries(currentSchool?.schoolID);
      dispatch(setJournalEntries(data));
    } catch (err) {
      setError('Failed to load journal entries');
      dispatch(setEntriesError(err.message));
      console.error('Journal entries error:', err);
    } finally {
      setLoading(false);
      dispatch(setEntriesLoading(false));
    }
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        description: entry.description,
        reference: entry.reference,
        date: entry.date.split('T')[0],
        entries: entry.entries || []
      });
    } else {
      setEditingEntry(null);
      setFormData({
        description: '',
        reference: '',
        date: new Date().toISOString().split('T')[0],
        entries: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEntry(null);
    setFormData({
      description: '',
      reference: '',
      date: new Date().toISOString().split('T')[0],
      entries: []
    });
  };

  const handleAddEntry = () => {
    setFormData({
      ...formData,
      entries: [...formData.entries, {
        accountCode: '',
        accountName: '',
        description: '',
        debit: 0,
        credit: 0,
        reference: ''
      }]
    });
  };

  const handleRemoveEntry = (index) => {
    const newEntries = formData.entries.filter((_, i) => i !== index);
    setFormData({ ...formData, entries: newEntries });
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    
    // Auto-fill account name when account code is selected
    if (field === 'accountCode') {
      const account = chartOfAccounts.find(acc => acc.accountCode === value);
      if (account) {
        newEntries[index].accountName = account.accountName;
      }
    }
    
    setFormData({ ...formData, entries: newEntries });
  };

  const handleSave = async () => {
    try {
      const validation = accountingService.validateJournalEntry(formData.entries);
      if (!validation.isBalanced) {
        setError(`Journal entry is not balanced. Difference: ${validation.difference}`);
        return;
      }

      const entryData = {
        ...formData,
        schoolID: currentSchool?.schoolID,
        createdBy: admin.userId,
        userName: admin.name,
        debitTotal: validation.debitTotal,
        creditTotal: validation.creditTotal
      };

      if (editingEntry) {
        const updatedEntry = await accountingService.updateJournalEntry(editingEntry._id, entryData);
        dispatch(updateJournalEntry(updatedEntry));
      } else {
        const newEntry = await accountingService.createJournalEntry(entryData);
        dispatch(addJournalEntry(newEntry));
      }

      handleCloseDialog();
    } catch (err) {
      setError('Failed to save journal entry');
      console.error('Save error:', err);
    }
  };

  const handlePost = async (entryId) => {
    try {
      const postedEntry = await accountingService.postJournalEntry(
        entryId, 
        admin.userId, 
        admin.name
      );
      dispatch(postJournalEntry(postedEntry));
    } catch (err) {
      setError('Failed to post journal entry');
      console.error('Post error:', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Posted': return 'success';
      case 'Draft': return 'warning';
      case 'Reversed': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => accountingService.formatCurrency(amount);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading journal entries...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadJournalEntries}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Journal Entries
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadJournalEntries} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Journal Entries Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entry #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Debit Total</TableCell>
                <TableCell align="right">Credit Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {journalEntries
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry) => (
                <TableRow key={entry._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {entry.entryNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {accountingService.formatDate(entry.date)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {entry.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.reference}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(entry.debitTotal)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(entry.creditTotal)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.status}
                      color={getStatusColor(entry.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog(entry)}
                          disabled={entry.status === 'Posted'}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {entry.status === 'Draft' && (
                        <Tooltip title="Post Entry">
                          <IconButton 
                            size="small" 
                            onClick={() => handlePost(entry._id)}
                            color="success"
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={journalEntries?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add Entry FAB */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog()}
      >
        <AddIcon />
      </Fab>

      {/* Journal Entry Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            
            {/* Journal Entry Items */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Journal Entry Items</Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddEntry}>
                  Add Entry
                </Button>
              </Box>
              
              {formData.entries.map((entry, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Account</InputLabel>
                          <Select
                            value={entry.accountCode}
                            onChange={(e) => handleEntryChange(index, 'accountCode', e.target.value)}
                            label="Account"
                          >
                            {chartOfAccounts.map((account) => (
                              <MenuItem key={account._id} value={account.accountCode}>
                                {account.accountCode} - {account.accountName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          value={entry.description}
                          onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Debit"
                          type="number"
                          value={entry.debit}
                          onChange={(e) => handleEntryChange(index, 'debit', parseFloat(e.target.value) || 0)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Credit"
                          type="number"
                          value={entry.credit}
                          onChange={(e) => handleEntryChange(index, 'credit', parseFloat(e.target.value) || 0)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <IconButton 
                          color="error" 
                          onClick={() => handleRemoveEntry(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {/* Balance Check */}
              {formData.entries && formData.entries.length > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2">
                    Debit Total: {formatCurrency(formData.entries.reduce((sum, entry) => sum + (entry.debit || 0), 0))}
                  </Typography>
                  <Typography variant="body2">
                    Credit Total: {formatCurrency(formData.entries.reduce((sum, entry) => sum + (entry.credit || 0), 0))}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={accountingService.validateJournalEntry(formData.entries).isBalanced ? 'success.main' : 'error.main'}
                  >
                    {accountingService.validateJournalEntry(formData.entries).isBalanced ? '✓ Balanced' : '✗ Not Balanced'}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingEntry ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JournalEntries;
